import React, { useState } from 'react';
import { BaseConfigProps, commonStyles } from '../shared/ConfigFormHelpers';
import CollapsibleSection from '../shared/CollapsibleSection';

const ErrorLoggerConfig: React.FC<BaseConfigProps> = ({ node, updateConfig }) => {
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set(['settings', 'destination']));
  
  const toggleSection = (section: string) => {
    setExpandedSections(prev => {
      const newSet = new Set(prev);
      if (newSet.has(section)) {
        newSet.delete(section);
      } else {
        newSet.add(section);
      }
      return newSet;
    });
  };

  const logLevels = [
    { value: 'error', label: 'Error', icon: '🔴', color: 'red' },
    { value: 'warning', label: 'Warning', icon: '🟡', color: 'yellow' },
    { value: 'info', label: 'Info', icon: '🔵', color: 'blue' },
    { value: 'debug', label: 'Debug', icon: '⚪', color: 'gray' },
  ];

  const destinations = [
    { value: 'console', label: 'Console', icon: '💻' },
    { value: 'file', label: 'Fájl', icon: '📄' },
    { value: 'database', label: 'Database', icon: '🗄️' },
    { value: 'external', label: 'Külső szolgáltatás', icon: '🌐' },
  ];

  return (
    <div className="space-y-3">
      {/* Beállítások */}
      <CollapsibleSection
        id="settings"
        title="Logger Beállítások"
        icon="⚙️"
        color="red"
        isExpanded={expandedSections.has('settings')}
        onToggle={toggleSection}
      >
        <div className="space-y-3">
          <div>
            <label className={commonStyles.label}>
              Log szint
            </label>
            <div className="grid grid-cols-2 gap-2">
              {logLevels.map((level) => (
                <button
                  key={level.value}
                  onClick={() => updateConfig('logLevel', level.value)}
                  className={`flex items-center justify-center p-2 rounded-md border text-sm transition-all ${
                    (node.data.config.logLevel || 'error') === level.value
                      ? `border-${level.color}-500 bg-${level.color}-50 dark:bg-${level.color}-900/20 text-${level.color}-700 dark:text-${level.color}-300`
                      : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:border-gray-400'
                  }`}
                >
                  <span className="text-lg mr-2">{level.icon}</span>
                  <span className="text-xs">{level.label}</span>
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className={commonStyles.label}>
              Log üzenet
            </label>
            <textarea
              value={node.data.config.message || ''}
              onChange={(e) => updateConfig('message', e.target.value)}
              className={commonStyles.textarea}
              rows={3}
              placeholder="Hiba történt: {{error.message}} - {{timestamp}}"
            />
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              Log üzenet változókkal
            </p>
          </div>

          <div className="flex items-center space-x-2">
            <input
              type="checkbox"
              id="includeTimestamp"
              checked={node.data.config.includeTimestamp !== false}
              onChange={(e) => updateConfig('includeTimestamp', e.target.checked)}
              className="h-4 w-4 text-red-600 focus:ring-red-500 border-gray-300 rounded"
            />
            <label htmlFor="includeTimestamp" className="text-sm text-gray-700 dark:text-gray-300">
              Időbélyeg hozzáadása
            </label>
          </div>

          <div className="flex items-center space-x-2">
            <input
              type="checkbox"
              id="includeContext"
              checked={node.data.config.includeContext !== false}
              onChange={(e) => updateConfig('includeContext', e.target.checked)}
              className="h-4 w-4 text-red-600 focus:ring-red-500 border-gray-300 rounded"
            />
            <label htmlFor="includeContext" className="text-sm text-gray-700 dark:text-gray-300">
              Kontextus információk
            </label>
          </div>
        </div>
      </CollapsibleSection>

      {/* Cél */}
      <CollapsibleSection
        id="destination"
        title="Log Célállomás"
        icon="📍"
        color="blue"
        isExpanded={expandedSections.has('destination')}
        onToggle={toggleSection}
      >
        <div className="space-y-3">
          <div>
            <label className={commonStyles.label}>
              Hova kerüljön a log
            </label>
            <div className="grid grid-cols-2 gap-2">
              {destinations.map((dest) => (
                <button
                  key={dest.value}
                  onClick={() => updateConfig('destination', dest.value)}
                  className={`flex items-center justify-center p-2 rounded-md border text-sm transition-all ${
                    (node.data.config.destination || 'console') === dest.value
                      ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300'
                      : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:border-gray-400'
                  }`}
                >
                  <span className="text-lg mr-2">{dest.icon}</span>
                  <span className="text-xs">{dest.label}</span>
                </button>
              ))}
            </div>
          </div>

          {node.data.config.destination === 'file' && (
            <div>
              <label className={commonStyles.label}>
                Fájl útvonal
              </label>
              <input
                type="text"
                value={node.data.config.filePath || ''}
                onChange={(e) => updateConfig('filePath', e.target.value)}
                className={commonStyles.input}
                placeholder="/var/log/app/errors.log"
              />
            </div>
          )}

          {node.data.config.destination === 'external' && (
            <div>
              <label className={commonStyles.label}>
                API Endpoint
              </label>
              <input
                type="text"
                value={node.data.config.apiEndpoint || ''}
                onChange={(e) => updateConfig('apiEndpoint', e.target.value)}
                className={commonStyles.input}
                placeholder="https://api.logging.com/v1/logs"
              />
            </div>
          )}
        </div>
      </CollapsibleSection>

      {/* Kimenet Változók */}
      <CollapsibleSection
        id="variables"
        title="Kimenet Változók"
        icon="🔗"
        color="purple"
        isExpanded={expandedSections.has('variables')}
        onToggle={toggleSection}
      >
        <div className="space-y-2">
          <div className="grid grid-cols-1 gap-2">
            {[
              { var: '{{logged}}', desc: 'Log sikeresen mentve', example: 'true' },
              { var: '{{logId}}', desc: 'Log azonosító', example: 'log_123456' },
              { var: '{{timestamp}}', desc: 'Időbélyeg', example: '2024-01-15 10:30:00' }
            ].map((item) => (
              <div key={item.var} className="bg-gray-50 dark:bg-gray-800 rounded-md p-2 border border-gray-200 dark:border-gray-700">
                <div className="flex items-center justify-between">
                  <code className="text-xs font-mono text-purple-600 dark:text-purple-400 bg-purple-50 dark:bg-purple-900/20 px-2 py-1 rounded">
                    {item.var}
                  </code>
                  <span className="text-xs text-gray-500 dark:text-gray-400 font-mono">
                    {item.example}
                  </span>
                </div>
                <div className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                  {item.desc}
                </div>
              </div>
            ))}
          </div>
        </div>
      </CollapsibleSection>

      {/* Példák */}
      <CollapsibleSection
        id="examples"
        title="Használati Példák"
        icon="💡"
        color="green"
        isExpanded={expandedSections.has('examples')}
        onToggle={toggleSection}
      >
        <div className="space-y-2 text-xs text-gray-600 dark:text-gray-400">
          <div className="bg-gray-50 dark:bg-gray-800 p-2 rounded border border-gray-200 dark:border-gray-700">
            <strong>❌ Hiba naplózás:</strong>
            <div className="mt-1 font-mono text-[10px]">
              Try-Catch → [error] Error Logger → Notification
            </div>
          </div>
          <div className="bg-gray-50 dark:bg-gray-800 p-2 rounded border border-gray-200 dark:border-gray-700">
            <strong>📊 Audit trail:</strong>
            <div className="mt-1 font-mono text-[10px]">
              Action → Error Logger (info) → Continue
            </div>
          </div>
        </div>
      </CollapsibleSection>
    </div>
  );
};

export default ErrorLoggerConfig;
